import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';

const FileUpload = ({
  accept = 'image/*',
  multiple = false,
  maxSize = 5242880,
  onUpload,
  label,
  description,
  error
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e?.dataTransfer?.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e?.target?.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    const validFiles = newFiles?.filter((file) => {
      if (file?.size > maxSize) {
        console.error(`File ${file?.name} exceeds maximum size`);
        return false;
      }
      return true;
    });

    if (!multiple && validFiles?.length > 0) {
      setFiles([validFiles?.[0]]);
      generatePreview(validFiles?.[0]);
    } else {
      setFiles((prev) => [...prev, ...validFiles]);
      validFiles?.forEach((file) => generatePreview(file));
    }

    if (onUpload) {
      validFiles?.forEach((file) => {
        simulateUpload(file);
      });
    }
  };

  const generatePreview = (file) => {
    if (file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => [...prev, { name: file?.name, url: reader?.result }]);
      };
      reader?.readAsDataURL(file);
    }
  };

  const simulateUpload = (file) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress((prev) => ({ ...prev, [file?.name]: progress }));

      if (progress >= 100) {
        clearInterval(interval);
        if (onUpload) {
          onUpload(file);
        }
      }
    }, 200);
  };

  const removeFile = (fileName) => {
    setFiles((prev) => prev?.filter((f) => f?.name !== fileName));
    setPreviews((prev) => prev?.filter((p) => p?.name !== fileName));
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress?.[fileName];
      return newProgress;
    });
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-text-primary">
          {label}
        </label>
      )}
      <div
        className={`
          file-upload-zone
          ${isDragging ? 'file-upload-zone-active' : ''}
          ${error ? 'border-error' : ''}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef?.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileInput}
          className="hidden"
        />

        <Icon name="Upload" size={32} className="mx-auto mb-4 text-text-secondary" />
        <p className="text-sm font-medium text-text-primary mb-1">
          Drop files here or click to browse
        </p>
        <p className="text-xs text-text-secondary">
          {description || `Maximum file size: ${(maxSize / 1048576)?.toFixed(0)}MB`}
        </p>
      </div>
      {error && (
        <p className="text-sm text-error">{error}</p>
      )}
      {previews?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {previews?.map((preview) => (
            <div key={preview?.name} className="relative group">
              <Image
                src={preview?.url}
                alt={preview?.name}
                className="w-full h-32 object-cover rounded-lg border border-border"
              />
              <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity duration-150 rounded-lg flex items-center justify-center">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={(e) => {
                    e?.stopPropagation();
                    removeFile(preview?.name);
                  }}
                  iconName="Trash2"
                  iconSize={14}
                >
                  Remove
                </Button>
              </div>
              {uploadProgress?.[preview?.name] !== undefined && uploadProgress?.[preview?.name] < 100 && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted rounded-b-lg overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-200"
                    style={{ width: `${uploadProgress?.[preview?.name]}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;